/**
 * The status a note about an order can carry beside its text (SSD-7609, FC request 13-3): the FC team's list of
 * Oct 21, in the order the picker offers it. The server refuses any other value.
 */
export const ORDER_COMMENT_STATUSES = [
  'DAMAGED',
  'REORDERED',
  'MISSING',
  'WRONG_ITEM',
  'RETURN_IN_PROGRESS',
  'RESOLVED',
  'STOCK_DECISION_NEEDED',
] as const;

export type OrderCommentStatus = (typeof ORDER_COMMENT_STATUSES)[number];

/** How each status reads on a chip; `tone` picks the chip colour (open issue, in progress, waiting on a decision, done). */
export const ORDER_COMMENT_STATUS_LABEL: Record<
  OrderCommentStatus,
  { label: string; tone: 'open' | 'moving' | 'decide' | 'done' }
> = {
  DAMAGED: { label: 'Damaged', tone: 'open' },
  REORDERED: { label: 'Reordered', tone: 'moving' },
  MISSING: { label: 'Missing', tone: 'open' },
  WRONG_ITEM: { label: 'Wrong item', tone: 'open' },
  RETURN_IN_PROGRESS: { label: 'Return in progress', tone: 'moving' },
  RESOLVED: { label: 'Resolved', tone: 'done' },
  STOCK_DECISION_NEEDED: { label: 'Stock decision needed', tone: 'decide' },
};

export const isOrderCommentStatus = (value: unknown): value is OrderCommentStatus =>
  typeof value === 'string' && (ORDER_COMMENT_STATUSES as readonly string[]).includes(value);

interface ThreadNote {
  lineNo?: number;
  status?: string;
  pinned?: boolean;
}

/** Where the order stands by its thread: the status of the latest order note that carries one, as the server reads it. */
export const orderStatusOf = (comments: readonly ThreadNote[]): OrderCommentStatus | null => {
  for (const comment of [...comments].reverse()) {
    if (comment.lineNo === undefined && isOrderCommentStatus(comment.status)) {
      return comment.status;
    }
  }
  return null;
};

/** The thread with its pinned note first and the rest in the order they were written. */
export const pinnedFirst = <T extends ThreadNote>(comments: readonly T[]): T[] => {
  const pinned = comments.filter((comment) => comment.pinned === true && comment.lineNo === undefined);
  return pinned.length === 0 ? [...comments] : [...pinned, ...comments.filter((comment) => !pinned.includes(comment))];
};
